import { useCallback, useEffect, useMemo, useRef, useState, type MouseEvent } from 'react';
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  Controls,
  Handle,
  Position,
  addEdge,
  applyNodeChanges,
  applyEdgeChanges,
  useNodesState,
  useEdgesState,
  type Connection,
  type Node,
  type Edge,
  type NodeChange,
  type EdgeChange,
  type NodeProps,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { X } from 'lucide-react';

import type { DiagramView, DiagramNode, DiagramEdge, Tone } from '@/features/Visualizer/types';
import { TONES } from '@/features/Visualizer/theme';

interface Props {
  value: DiagramView;
  onChange: (next: DiagramView) => void;
}

const SHAPES: Array<{ value: NonNullable<DiagramNode['shape']>; label: string }> = [
  { value: 'box', label: 'Box' },
  { value: 'circle', label: 'Circle' },
  { value: 'diamond', label: 'Diamond' },
];

const TONE_OPTIONS: Tone[] = ['accent', 'info', 'success', 'warn', 'muted'];

interface ShapeNodeData {
  node: DiagramNode;
}

/**
 * The one custom node type this canvas registers — a compact, read-only
 * preview of the node's shape/label/attribute count. Unlike
 * `VisualizationTreeGraphCanvas`'s `LabelNode`, editing does NOT happen
 * inline on the node itself: a generic node can carry a label, a shape, a
 * color, AND an open-ended list of attributes, which is too much to fit (or
 * usefully edit) inside a small canvas box. Selecting the node opens the
 * properties panel (`NodePropertiesPanel`) instead — the same pattern
 * Figma/n8n use for editing an arbitrary selected object's fields.
 */
function ShapeNode({ data }: NodeProps) {
  const { node } = data as unknown as ShapeNodeData;
  const color = TONES[node.tone ?? 'muted'];
  const attrCount = Object.keys(node.attributes ?? {}).length;
  const shapeClass =
    node.shape === 'circle' ? 'rounded-full' : node.shape === 'diamond' ? 'rotate-45 rounded-sm' : 'rounded-md';
  return (
    <div
      className={`flex h-16 w-32 flex-col items-center justify-center border-2 bg-white px-2 text-center shadow-sm ${shapeClass}`}
      style={{ borderColor: color }}
    >
      <Handle type="target" position={Position.Top} />
      <div className={node.shape === 'diamond' ? '-rotate-45' : ''}>
        <p className="truncate text-xs font-semibold text-[#1c1917]">{node.label || '(untitled)'}</p>
        {attrCount > 0 && <p className="text-[10px] text-[#9ca3af]">{attrCount} attribute{attrCount === 1 ? '' : 's'}</p>}
      </div>
      <Handle type="source" position={Position.Bottom} />
    </div>
  );
}

const nodeTypes = { shapeNode: ShapeNode };

function nextId(existingIds: string[], prefix: string): string {
  const used = new Set(existingIds);
  let i = existingIds.length + 1;
  while (used.has(`${prefix}-${i}`)) i += 1;
  return `${prefix}-${i}`;
}

function layoutNodes(value: DiagramView): Node[] {
  return value.nodes.map((n) => ({
    id: n.id,
    type: 'shapeNode',
    // Position IS the persisted `x`/`y` this time — see `DiagramNode`'s doc
    // comment in `Visualizer/types.ts` on why a freeform diagram is the one
    // view where geometry is real authored content, not something a
    // renderer derives.
    position: { x: n.x, y: n.y },
    data: { node: n } satisfies ShapeNodeData,
  }));
}

function edgesFromValue(value: DiagramView): Edge[] {
  return value.edges.map((e) => ({
    id: e.id,
    source: e.source,
    target: e.target,
    label: e.label,
    markerEnd: e.directed ? { type: 'arrowclosed' as const } : undefined,
  }));
}

/** Properties panel for whichever node or edge is currently selected — floats over the canvas rather than a fixed sidebar, so it doesn't shrink the drawing area for trainers with many nodes. */
function NodePropertiesPanel({
  node,
  onUpdate,
  onDelete,
  onClose,
}: {
  node: DiagramNode;
  onUpdate: (patch: Partial<DiagramNode>) => void;
  onDelete: () => void;
  onClose: () => void;
}) {
  const entries = Object.entries(node.attributes ?? {});

  const emitAttributes = (next: Array<[string, string]>) => {
    onUpdate({ attributes: Object.fromEntries(next) });
  };

  return (
    <div className="absolute right-2 top-2 z-10 w-64 rounded-md border border-[#d1d5db] bg-white p-3 shadow-lg">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-xs font-semibold text-[#374151]">Node</span>
        <button type="button" onClick={onClose} aria-label="Close node properties" className="text-[#6b7280]">
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
      <label className="mb-1 block text-[11px] font-medium text-[#374151]" htmlFor="diagram-node-label">
        Label
      </label>
      <input
        id="diagram-node-label"
        value={node.label}
        onChange={(e) => onUpdate({ label: e.target.value })}
        className="mb-2 w-full rounded-md border border-[#d1d5db] px-2 py-1 text-sm"
      />
      <label className="mb-1 block text-[11px] font-medium text-[#374151]" htmlFor="diagram-node-shape">
        Shape
      </label>
      <select
        id="diagram-node-shape"
        value={node.shape ?? 'box'}
        onChange={(e) => onUpdate({ shape: e.target.value as DiagramNode['shape'] })}
        className="mb-2 w-full rounded-md border border-[#d1d5db] bg-white px-2 py-1 text-sm"
      >
        {SHAPES.map((s) => (
          <option key={s.value} value={s.value}>
            {s.label}
          </option>
        ))}
      </select>
      <p className="mb-1 text-[11px] font-medium text-[#374151]">Color</p>
      <div className="mb-2 flex gap-1.5">
        {TONE_OPTIONS.map((tone) => (
          <button
            key={tone}
            type="button"
            aria-label={`Set color ${tone}`}
            onClick={() => onUpdate({ tone })}
            className={`h-5 w-5 rounded-full border-2 ${(node.tone ?? 'muted') === tone ? 'border-[#1c1917]' : 'border-transparent'}`}
            style={{ backgroundColor: TONES[tone] }}
          />
        ))}
      </div>
      <div className="mb-1 flex items-center justify-between">
        <span className="text-[11px] font-medium text-[#374151]">Attributes</span>
        <button
          type="button"
          className="text-[11px] text-primary hover:underline"
          onClick={() => emitAttributes([...entries, ['', '']])}
        >
          + Add
        </button>
      </div>
      {entries.map(([key, value], index) => (
        <div key={index} className="mb-1 flex items-center gap-1">
          <input
            value={key}
            placeholder="name"
            onChange={(e) => {
              const next = [...entries];
              next[index] = [e.target.value, next[index][1]];
              emitAttributes(next);
            }}
            className="w-20 rounded-md border border-[#d1d5db] px-1.5 py-1 text-xs"
          />
          <input
            value={value}
            placeholder="value"
            onChange={(e) => {
              const next = [...entries];
              next[index] = [next[index][0], e.target.value];
              emitAttributes(next);
            }}
            className="w-full rounded-md border border-[#d1d5db] px-1.5 py-1 text-xs"
          />
          <button
            type="button"
            aria-label={`Remove attribute ${key || `#${index + 1}`}`}
            onClick={() => emitAttributes(entries.filter((_, i) => i !== index))}
            className="shrink-0 text-destructive"
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={onDelete}
        className="mt-2 w-full rounded-md border border-destructive/50 py-1 text-xs text-destructive hover:bg-destructive/10"
      >
        Delete node
      </button>
    </div>
  );
}

function EdgePropertiesPanel({
  edge,
  onUpdate,
  onDelete,
  onClose,
}: {
  edge: DiagramEdge;
  onUpdate: (patch: Partial<DiagramEdge>) => void;
  onDelete: () => void;
  onClose: () => void;
}) {
  return (
    <div className="absolute right-2 top-2 z-10 w-64 rounded-md border border-[#d1d5db] bg-white p-3 shadow-lg">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-xs font-semibold text-[#374151]">Connection</span>
        <button type="button" onClick={onClose} aria-label="Close connection properties" className="text-[#6b7280]">
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
      <label className="mb-1 block text-[11px] font-medium text-[#374151]" htmlFor="diagram-edge-label">
        Label
      </label>
      <input
        id="diagram-edge-label"
        value={edge.label ?? ''}
        onChange={(e) => onUpdate({ label: e.target.value })}
        placeholder="e.g. sends request to"
        className="mb-2 w-full rounded-md border border-[#d1d5db] px-2 py-1 text-sm"
      />
      <label className="mb-2 flex items-center gap-2 text-[11px] text-[#374151]">
        <input
          type="checkbox"
          checked={!!edge.directed}
          onChange={(e) => onUpdate({ directed: e.target.checked })}
          className="h-3.5 w-3.5"
        />
        Directed (show an arrowhead)
      </label>
      <button
        type="button"
        onClick={onDelete}
        className="w-full rounded-md border border-destructive/50 py-1 text-xs text-destructive hover:bg-destructive/10"
      >
        Delete connection
      </button>
    </div>
  );
}

function Canvas({ value, onChange }: Props) {
  const [nodes, setNodes] = useNodesState(layoutNodes(value));
  const [edges, setEdges] = useEdgesState(edgesFromValue(value));

  // Same authoritative-ref pattern as `VisualizationTreeGraphCanvas` — see
  // that file's comment on why `nodes`/`edges` state alone isn't safe to
  // read from inside handlers that can fire back-to-back in one tick.
  const nodesRef = useRef(nodes);
  nodesRef.current = nodes;
  const edgesRef = useRef(edges);
  edgesRef.current = edges;

  const lastSyncedSerialized = useRef<string>(JSON.stringify(value));

  const emit = useCallback(
    (nextNodes: Node[], nextEdges: Edge[]) => {
      const next: DiagramView = {
        ...value,
        kind: 'diagram',
        nodes: nextNodes.map((n) => ({ ...(n.data as unknown as ShapeNodeData).node, id: n.id, x: n.position.x, y: n.position.y })),
        edges: nextEdges.map((e) => {
          const prev = value.edges.find((pe) => pe.id === e.id);
          return { ...prev, id: e.id, source: e.source, target: e.target };
        }),
      };
      lastSyncedSerialized.current = JSON.stringify(next);
      onChange(next);
    },
    [value, onChange]
  );

  useEffect(() => {
    const incomingSerialized = JSON.stringify(value);
    if (incomingSerialized === lastSyncedSerialized.current) return;
    lastSyncedSerialized.current = incomingSerialized;
    const nextNodes = layoutNodes(value);
    const nextEdges = edgesFromValue(value);
    nodesRef.current = nextNodes;
    edgesRef.current = nextEdges;
    setNodes(nextNodes);
    setEdges(nextEdges);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const onConnect = useCallback(
    (connection: Connection) => {
      const id = nextId(edgesRef.current.map((e) => e.id), 'edge');
      const nextEdges = addEdge({ ...connection, id }, edgesRef.current);
      edgesRef.current = nextEdges;
      setEdges(nextEdges);
      emit(nodesRef.current, nextEdges);
    },
    [setEdges, emit]
  );

  const onNodesChange = useCallback(
    (changes: NodeChange[]) => {
      const nextNodes = applyNodeChanges(changes, nodesRef.current);
      nodesRef.current = nextNodes;
      setNodes(nextNodes);

      // Position changes (drag end) and removals both need to reach the
      // persisted script — a drag's final `position` change has
      // `dragging: false`; intermediate moves during the drag are skipped
      // so we don't spam `onChange` on every pixel of motion.
      const removedIds = changes.filter((c) => c.type === 'remove').map((c) => c.id);
      const settled = changes.some((c) => c.type === 'position' && c.dragging === false);
      if (removedIds.length > 0) {
        const removedSet = new Set(removedIds);
        const nextEdges = edgesRef.current.filter((e) => !removedSet.has(e.source) && !removedSet.has(e.target));
        edgesRef.current = nextEdges;
        setEdges(nextEdges);
        emit(nextNodes, nextEdges);
      } else if (settled) {
        emit(nextNodes, edgesRef.current);
      }
    },
    [setNodes, setEdges, emit]
  );

  const onEdgesChange = useCallback(
    (changes: EdgeChange[]) => {
      const nextEdges = applyEdgeChanges(changes, edgesRef.current);
      edgesRef.current = nextEdges;
      setEdges(nextEdges);
      if (changes.some((c) => c.type === 'remove')) {
        emit(nodesRef.current, nextEdges);
      }
    },
    [setEdges, emit]
  );

  const addNode = useCallback(() => {
    const id = nextId(nodesRef.current.map((n) => n.id), 'node');
    const i = nodesRef.current.length;
    const newNode: Node = {
      id,
      type: 'shapeNode',
      position: { x: 40 + 180 * (i % 4), y: 40 + 120 * Math.floor(i / 4) },
      data: { node: { id, label: 'New node', shape: 'box', tone: 'muted', attributes: {}, x: 0, y: 0 } } satisfies ShapeNodeData,
    };
    const nextNodes = [...nodesRef.current, newNode];
    nodesRef.current = nextNodes;
    setNodes(nextNodes);
    emit(nextNodes, edgesRef.current);
  }, [setNodes, emit]);

  /** Same click-to-connect pattern as `VisualizationTreeGraphCanvas` — proven reliable on trackpads that can't drag between handles. */
  const [connectMode, setConnectMode] = useState(false);
  const [pendingSourceId, setPendingSourceId] = useState<string | null>(null);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [selectedEdgeId, setSelectedEdgeId] = useState<string | null>(null);

  const onNodeClick = useCallback(
    (_event: MouseEvent, node: Node) => {
      if (connectMode) {
        if (!pendingSourceId) {
          setPendingSourceId(node.id);
          return;
        }
        if (pendingSourceId === node.id) {
          setPendingSourceId(null);
          return;
        }
        const id = nextId(edgesRef.current.map((e) => e.id), 'edge');
        const nextEdges = addEdge({ source: pendingSourceId, target: node.id, id, sourceHandle: null, targetHandle: null }, edgesRef.current);
        edgesRef.current = nextEdges;
        setEdges(nextEdges);
        emit(nodesRef.current, nextEdges);
        setPendingSourceId(null);
        return;
      }
      setSelectedEdgeId(null);
      setSelectedNodeId(node.id);
    },
    [connectMode, pendingSourceId, setEdges, emit]
  );

  const onEdgeClick = useCallback((_event: MouseEvent, edge: Edge) => {
    setSelectedNodeId(null);
    setSelectedEdgeId(edge.id);
  }, []);

  const updateNode = useCallback(
    (id: string, patch: Partial<DiagramNode>) => {
      const nextNodes = nodesRef.current.map((n) =>
        n.id === id ? { ...n, data: { node: { ...(n.data as unknown as ShapeNodeData).node, ...patch } } } : n
      );
      nodesRef.current = nextNodes;
      setNodes(nextNodes);
      emit(nextNodes, edgesRef.current);
    },
    [setNodes, emit]
  );

  const deleteNode = useCallback(
    (id: string) => {
      const nextNodes = nodesRef.current.filter((n) => n.id !== id);
      const nextEdges = edgesRef.current.filter((e) => e.source !== id && e.target !== id);
      nodesRef.current = nextNodes;
      edgesRef.current = nextEdges;
      setNodes(nextNodes);
      setEdges(nextEdges);
      emit(nextNodes, nextEdges);
      setSelectedNodeId(null);
    },
    [setNodes, setEdges, emit]
  );

  const updateEdge = useCallback(
    (id: string, patch: Partial<DiagramEdge>) => {
      const nextEdges = edgesRef.current.map((e) => (e.id === id ? { ...e, ...patch } : e));
      edgesRef.current = nextEdges;
      setEdges(nextEdges);
      // `edges` state (React Flow's own shape) doesn't carry `directed` —
      // that only exists on the persisted `DiagramEdge`, so `emit` reads it
      // back from `value.edges` merged with this patch rather than from
      // `nextEdges` itself.
      const next: DiagramView = {
        ...value,
        kind: 'diagram',
        edges: value.edges.map((e) => (e.id === id ? { ...e, ...patch } : e)),
      };
      lastSyncedSerialized.current = JSON.stringify(next);
      onChange(next);
    },
    [setEdges, value, onChange]
  );

  const deleteEdge = useCallback(
    (id: string) => {
      const nextEdges = edgesRef.current.filter((e) => e.id !== id);
      edgesRef.current = nextEdges;
      setEdges(nextEdges);
      emit(nodesRef.current, nextEdges);
      setSelectedEdgeId(null);
    },
    [setEdges, emit]
  );

  const selectedNode = useMemo(
    () => (selectedNodeId ? value.nodes.find((n) => n.id === selectedNodeId) ?? null : null),
    [selectedNodeId, value.nodes]
  );
  const selectedEdge = useMemo(
    () => (selectedEdgeId ? value.edges.find((e) => e.id === selectedEdgeId) ?? null : null),
    [selectedEdgeId, value.edges]
  );

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={addNode}
          className="rounded-md border border-dashed border-primary/50 bg-primary/5 px-2 py-1 text-xs font-medium text-primary hover:bg-primary/10"
        >
          + Add node
        </button>
        <button
          type="button"
          onClick={() => {
            setConnectMode((on) => !on);
            setPendingSourceId(null);
          }}
          className={`rounded-md border px-2 py-1 text-xs font-medium ${
            connectMode
              ? 'border-primary bg-primary text-white'
              : 'border-dashed border-primary/50 bg-primary/5 text-primary hover:bg-primary/10'
          }`}
        >
          {connectMode ? (pendingSourceId ? 'Click a node to connect to…' : 'Click two nodes to connect') : 'Connect nodes'}
        </button>
        <span className="text-[11px] text-[#9ca3af]">Click a node or connection to edit it · drag nodes to arrange your diagram</span>
      </div>
      <div className="relative" style={{ height: 420 }}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={nodeTypes}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onNodeClick={onNodeClick}
          onEdgeClick={onEdgeClick}
          onPaneClick={() => {
            setSelectedNodeId(null);
            setSelectedEdgeId(null);
          }}
          className="rounded-md border border-[#ececec]"
        >
          <Background />
          <Controls position="top-right" showInteractive={false} />
        </ReactFlow>
        {selectedNode && (
          <NodePropertiesPanel
            node={selectedNode}
            onUpdate={(patch) => updateNode(selectedNode.id, patch)}
            onDelete={() => deleteNode(selectedNode.id)}
            onClose={() => setSelectedNodeId(null)}
          />
        )}
        {selectedEdge && (
          <EdgePropertiesPanel
            edge={selectedEdge}
            onUpdate={(patch) => updateEdge(selectedEdge.id, patch)}
            onDelete={() => deleteEdge(selectedEdge.id)}
            onClose={() => setSelectedEdgeId(null)}
          />
        )}
      </div>
    </div>
  );
}

export function VisualizationDiagramCanvas(props: Props) {
  return (
    <ReactFlowProvider>
      <Canvas {...props} />
    </ReactFlowProvider>
  );
}
